import styled, { css } from 'styled-components';
import { mqMediumMin } from 'styled/tools/media-queries';
import { FontFamily } from 'styled/fonts/font-family';

type TypographyProps = {
  color?: string;
};

const TypographyBase = styled.p<TypographyProps>`
  ${({ color = 'primary' }) => `color: var(--text-${color});`}
`;

TypographyBase.defaultProps = {
  color: 'primary',
};

const type = css`
  font-family: var(--ff, ${FontFamily.sans});
  font-size: var(--fz, 1rem);
  font-weight: var(--fw, inherit);
  letter-spacing: var(--lts, normal);
  line-height: var(--lh, inherit);
  text-transform: var(--tt, none);
`;

const TypeHeadingPrimary = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-1000);
  --lh: 1.1;
  --lts: -0.025em;

  @media ${mqMediumMin} {
    --fz: var(--font-size-1200);
  }
`;

const TypeHeadingSecondary = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-800);
  --lh: 1.1;
  --lts: -0.015em;

  @media ${mqMediumMin} {
    --fz: var(--font-size-1200);
  }
`;

const TypeHeadingTertiary = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: 28px;
  --lh: 1.1;
  --lts: -0.01em;

  @media ${mqMediumMin} {
    --fz: var(--font-size-1000);
  }
`;

const TypeHeadingQuarternary = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-600);
  --lh: 1.2;
  --lts: -0.01em;

  @media ${mqMediumMin} {
    --fz: var(--font-size-800);
    --lh: 1.1;
  }
`;

const TypeSubhead = styled(TypographyBase)`
  ${type}
  --fw: 400;
  --fz: 28px;
  --lh: 1.1;
  --lts: -0.01em;
  @media ${mqMediumMin} {
    --fz: var(--font-size-800);
  }
`;

const TypePageSubhead = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-600);
  --lh: 1.2;
  --lts: -0.02em;
`;

const TypeBodyLarge = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-500);
  --lh: 1.3;
  --lts: -0.01em;
`;

const TypeBodyLargeStrong = styled(TypographyBase)`
  ${type}
  --fw: 500;
  --fz: var(--font-size-400);
  --lh: 1.3;
  --lts: -0.01em;

  @media ${mqMediumMin} {
    --fz: var(--font-size-500);
  }
`;

const TypeBody = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-400);
  --lh: 1.3;
  --lts: -0.01em;
`;

const TypeEyebrow = styled.p`
  ${type}
  --fw: 500;
  --fz: var(--font-size-300);
  --tt: uppercase;

  font-family: var(--ff-sans-medium, ${FontFamily.sansMedium});
`;

const TypeLabel = styled(TypographyBase)`
  ${type}
  --fw: 300;
  --fz: var(--font-size-300);
  --tt: uppercase;

  font-family: var(--ff-sans-mono, ${FontFamily.mono});
`;

const TypeCaption = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-300);

  @media ${mqMediumMin} {
    --fz: var(--font-size-400);
  }
  --lh: 1.4;
`;

const TypeDisclaimer = styled(TypographyBase)`
  ${type}
  --fw: 350;
  --fz: var(--font-size-300);
  --lh: 1.4;
  --lts: -0.003em;
`;

export {
  TypeHeadingPrimary,
  TypeHeadingSecondary,
  TypeHeadingTertiary,
  TypeHeadingQuarternary,
  TypeSubhead,
  TypePageSubhead,
  TypeBodyLarge,
  TypeBodyLargeStrong,
  TypeBody,
  TypeEyebrow,
  TypeCaption,
  TypeDisclaimer,
  TypeLabel,
};
